import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { EmployeeService } from '../services/employee.service';
import * as EmployeeActions from './employee.actions';

@Injectable()
export class EmployeeEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _employeeService = inject(EmployeeService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all employees',
        conectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Employees
    getAllEmployees$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeActions.loadEmployees),
            switchMap(() => {
                return this._employeeService.getAll().pipe(
                    tap((res) => {
                        this.logger.log('res', res);
                    }),
                    map((res) => {
                        return EmployeeActions.loadEmployeesSuccess({
                            employees: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeActions.loadEmployeesFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Employee
    getEmployee$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeActions.loadEmployee),
            switchMap((action) => {
                return this._employeeService.getOne(action.id).pipe(
                    map((res) => {
                        return EmployeeActions.loadEmployeeSuccess({
                            employee: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeActions.loadEmployeeFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Employee
    createEmployee$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeActions.createEmployee),
            switchMap((action) => {
                return this._employeeService.create(action.data).pipe(
                    map((res) => {
                        return EmployeeActions.createEmployeeSuccess({
                            employee: res.data,
                            success: 'Employee created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeActions.createEmployeeFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Employee
    updateEmployee$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeActions.updateEmployee),
            switchMap((action) => {
                return this._employeeService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return EmployeeActions.updateEmployeeSuccess({
                                employee: res.data,
                                success: 'Employee updated successfully',
                            });
                        }),
                        catchError((error) =>
                            of(
                                EmployeeActions.updateEmployeeFailure({
                                    error:
                                        error?.error?.message ||
                                        'Unknown error',
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete Employee
    deleteEmployee$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeActions.deleteEmployee),
            switchMap((action) => {
                return this._employeeService.delete(action.id).pipe(
                    map(() => {
                        return EmployeeActions.deleteEmployeeSuccess({
                            id: action.id,
                            success: 'Employee deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeActions.deleteEmployeeFailure({
                                error: error?.error?.message || 'Unknown error',
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onEmployeeActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    EmployeeActions.createEmployeeSuccess,
                    EmployeeActions.updateEmployeeSuccess,
                    EmployeeActions.deleteEmployeeSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onEmployeeActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    EmployeeActions.loadEmployeesFailure,
                    EmployeeActions.loadEmployeeFailure,
                    EmployeeActions.createEmployeeFailure,
                    EmployeeActions.updateEmployeeFailure,
                    EmployeeActions.deleteEmployeeFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
